import { absenceCategoryTranslations } from "~components/shared/absenceCategoryTranslations.ts";
import { absenceCategoryForTrackingTaskType } from "~components/TimeManagement/shared/trackingTaskTypeHelpers.ts";
import type { EmbeddedTimeEntry } from "~components/TimeManagement/shared/useEmbeddedTimeEntries/EmbeddedTimeEntry.ts";
import type { Translations } from "~contexts/I18nContext/I18nContext.tsx";
import { getResourceName } from "~lib/resourceHelpers.ts";
import { GroupingOption } from "./GroupingOption.ts";

type GroupName = (timeEntry: EmbeddedTimeEntry, t: Translations) => string;

interface Grouping {
  label: (t: Translations) => string;
  groupNameFilterLabel: (t: Translations) => string;
  groupName: GroupName;
  groupKey: GroupName;
  groupsManagerAccountBookingsByTracking: boolean;
}

export function managerAccountGroupKey(timeEntry: EmbeddedTimeEntry) {
  if (!timeEntry.isConstructionSiteManagerAccount) {
    return undefined;
  }
  return `${timeEntry.time_tracking_id}:${timeEntry.construction_site_manager_id}`;
}

function managerAccountGroupName(
  timeEntry: EmbeddedTimeEntry,
  t: Translations,
) {
  const customerName = timeEntry.task?.customer_name;
  if (!customerName) {
    return t.timeApprovalManagerAccountGroupWithoutTask;
  }
  return t.timeApprovalManagerAccountGroup.replace("{customer}", customerName);
}

function byTrackingForManagerAccountBookings(
  groupName: GroupName,
): Pick<
  Grouping,
  "groupName" | "groupKey" | "groupsManagerAccountBookingsByTracking"
> {
  return {
    groupName: (timeEntry, t) =>
      timeEntry.isConstructionSiteManagerAccount
        ? managerAccountGroupName(timeEntry, t)
        : groupName(timeEntry, t),
    groupKey: (timeEntry, t) =>
      managerAccountGroupKey(timeEntry) ?? groupName(timeEntry, t),
    groupsManagerAccountBookingsByTracking: true,
  };
}

export function teamGroupName(timeEntry: EmbeddedTimeEntry, t: Translations) {
  return timeEntry.original_team_name || t.timeApprovalNoTeam;
}

function taskGroupName(timeEntry: EmbeddedTimeEntry, t: Translations) {
  if (!timeEntry.isTrackingTaskTypeWork) {
    const absenceCategory = absenceCategoryForTrackingTaskType(
      timeEntry.tracking_task_type,
    );
    return t[absenceCategoryTranslations[absenceCategory]];
  }
  return `${timeEntry.workPackage?.name} (${timeEntry.constructionSite?.number})`;
}

function resourceGroupName(timeEntry: EmbeddedTimeEntry) {
  return getResourceName(timeEntry.resource);
}

export const groupings: Record<GroupingOption, Grouping> = {
  [GroupingOption.Team]: {
    label: (t) => t.timeApprovalFiltersGroupingTeam,
    groupNameFilterLabel: (t) => t.timeApprovalFiltersGroupNameTeam,
    ...byTrackingForManagerAccountBookings(teamGroupName),
  },
  [GroupingOption.Task]: {
    label: (t) => t.timeApprovalFiltersGroupingWorkPackage,
    groupNameFilterLabel: (t) => t.timeApprovalFiltersGroupNameWorkPackage,
    ...byTrackingForManagerAccountBookings(taskGroupName),
  },
  [GroupingOption.Resource]: {
    label: (t) => t.timeApprovalFiltersGroupingEmployee,
    groupNameFilterLabel: (t) => t.timeApprovalFiltersGroupNameEmployee,
    groupName: resourceGroupName,
    groupKey: resourceGroupName,
    groupsManagerAccountBookingsByTracking: false,
  },
};
